<template>
  <div class="surface-section">
    <div class="flex">
      <div style="flex: 1">
        <div class="font-bold text-900 text-2xl mt-5 mb-2">{{ pageTitle }}</div>
        <div class="text-700 mb-5 line-height-3 mb-5">{{ pageDescription }}.</div>
      </div>
      <div class="flex gap-2 justify-content-end align-items-center">
        <Button @click="fetchEventLog" label="Refresh" :loading="loading" icon="pi pi-refresh" />
      </div>
    </div>

    <Loading v-if="!eventLog && loading" :title="'Loading event log'" :full="false" />

    <div v-if="eventLog" class="relative">
      <!-- Loading overlay for page/filter changes -->
      <div v-if="loading" class="loading-overlay">
        <div class="flex flex-column align-items-center gap-2">
          <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
          <span class="text-color-secondary font-medium">Loading events...</span>
        </div>
      </div>

      <!-- Pagination summary -->
      <div class="mb-3 flex align-items-center gap-3">
        <span class="text-color-secondary text-sm">{{ paginationSummary }}</span>
      </div>

      <!-- Timeline -->
      <Timeline
        v-if="eventLog.length > 0"
        :value="eventLog"
        v-auto-animate
        dataKey="eventId"
        class="z-0"
        :pt="{
          opposite: 'font-bold text-xl'
        }"
      >
        <template #marker>
          <span class="flex w-2rem h-2rem align-items-center justify-content-center border-circle z-1 shadow-1 text-grey-200">
            <i class="pi pi-clock"></i>
          </span>
        </template>
        <template #opposite="slotProps">
          <div :key="slotProps.item.eventId">
            <div class="mt-2">
              <small class="text-color-secondary text-sm">{{ formatDateTimeFullTimezone(slotProps.item.timestamp) }}</small>
            </div>
            <div class="mt-2">
              <small class="font-bold text-sm" :class="'aggregate-color-' + slotProps.item.eventAggregateSource">
                {{ slotProps.item.eventAggregateSource }}
                <i :class="aggregateSourceToIcon(slotProps.item.eventAggregateSource)"></i>
              </small>
            </div>
            <div>
              <small class="font-bold font-italic text-sm">{{ aggregateIdentityLookup[slotProps.item.mainAggregateId] }}</small>
            </div>
          </div>
        </template>
        <template #content="slotProps">
          <div class="relative" :key="slotProps.item.eventId">
            <Card class="mb-4 shadow-3 border-round-lg">
              <template #title>
                <div class="flex flex-row justify-content-between align-items-end">
                  <div>
                    {{ slotProps.item.eventName }}
                  </div>
                  <div class="ml-2 mb-1">
                    <Tag :value="slotProps.item.eventSource" severity="info" class="text-xs" />
                  </div>
                </div>
              </template>
              <template #subtitle>
                {{ slotProps.item.eventDescription }}
              </template>
              <template #content>
                <div style="min-width: 38rem; width: 100%">
                  <Panel toggleable header="Properties" :collapsed="true">
                    <DataTable :value="slotProps.item.changes">
                      <Column field="propertyName" header="Property">
                        <template #body="slotProps">
                          <div>
                            <b>{{ slotProps.data.propertyName }}</b>
                          </div>
                          <div>
                            <i>{{ slotProps.data.propertyComment }}</i>
                          </div>
                        </template>
                      </Column>
                      <Column
                        field="value"
                        header="Value"
                        style="white-space: pre-line; word-break: break-word; font-family: Consolas, monospace"
                      ></Column>
                    </DataTable>
                  </Panel>
                </div>
              </template>
            </Card>
            <div
              class="absolute left-0 top-0 bottom-0 border-round-left-lg"
              style="width: 6px"
              :class="'aggregate-bgcolor-' + slotProps.item.eventAggregateSource"
            ></div>
          </div>
        </template>
      </Timeline>

      <div v-else class="p-5 text-center text-color-secondary">
        <i class="pi pi-inbox text-4xl mb-3" style="display: block"></i>
        <span>No events found matching the current filters.</span>
      </div>

      <!-- Paginator -->
      <Paginator
        v-if="totalCount > 0"
        :rows="pageSize"
        :totalRecords="totalCount"
        :first="(currentPage - 1) * pageSize"
        @page="onPageChange"
        class="mt-3 mb-3"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
      />

      <!-- Filters -->
      <div class="fixed z-0 w-24rem filters-card" :class="{ collapsed: filtersCollapsed }">
        <Card class="shadow-4 border-round-right-lg">
          <template #header>
            <div class="flex justify-content-between align-items-end">
              <div class="font-bold text-900 text-2xl pt-4 pl-3">
                <span class="fa fa-cog mr-2"></span>
                <span>Filters</span>
              </div>

              <div class="mr-2 relative">
                <Button
                  icon="fa fa-chevron-left"
                  severity="secondary"
                  text
                  class="absolute right-0 bottom-0"
                  :class="{ hidden: filtersCollapsed }"
                  @click="filtersCollapsed = !filtersCollapsed"
                />
                <Button
                  icon="fa fa-cog"
                  severity="secondary"
                  text
                  :class="{ hidden: !filtersCollapsed }"
                  class="absolute right-0 bottom-0"
                  @click="filtersCollapsed = !filtersCollapsed"
                />
              </div>
            </div>
          </template>
          <template #content>
            <div class="flex flex-column align-items-start justify-content-start filters-content">
              <div class="flex gap-2">
                <div>
                  <label for="fromDate" class="text-sm pb-1">From date</label>
                  <Calendar id="fromDate" v-model="fromDate" dateFormat="dd-mm-yy" showTime showButtonBar />
                </div>

                <div>
                  <label for="toDate" class="text-sm pb-1">To date</label>
                  <Calendar id="toDate" v-model="toDate" dateFormat="dd-mm-yy" showTime showButtonBar />
                </div>
              </div>

              <label class="text-sm mt-3 pb-1">Event aggregate source</label>
              <TreeSelect
                v-model="selectedAggregateSources"
                :options="aggregateSourcesTreeNode"
                placeholder="Select aggregate sources"
                :maxSelectedLabels="3"
                class="w-full md:w-22rem"
                selectionMode="checkbox"
              />

              <label class="text-sm mt-3 pb-1">Event origin</label>
              <MultiSelect
                v-model="selectedAggregateOrigins"
                :options="aggregateOrigins"
                placeholder="Select aggregate origins"
                :maxSelectedLabels="3"
                class="w-full md:w-22rem"
                selectionMode="checkbox"
              />
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted, watch, onBeforeUnmount } from "vue";

import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Paginator from "primevue/paginator";

export default {
  inject: ["hasPermission"],
  components: { Paginator },
  setup() {
    const pageTitle = "Event log";
    const pageDescription = "The event sourcing log for this user";
    const PAGE_SIZE = 25;

    const loading = ref(false);

    const store = useStore();
    const route = useRoute();

    const useFormatDate = inject("useFormatDate");
    const fromDate = ref(null);
    const toDate = ref(null);

    const userId = route.params.userId;

    const eventLog = ref(null);
    const aggregateIdentityLookup = ref({});
    const aggregateSources = ref([]);
    const selectedAggregateSources = ref({});

    const aggregateOrigins = ref(["BackOffice", "MobileApp", "General"]);
    const selectedAggregateOrigins = ref([]);

    const currentPage = ref(1);
    const pageSize = ref(PAGE_SIZE);
    const totalCount = ref(0);
    const totalPages = ref(0);

    const filtersCollapsed = ref(false);

    const paginationSummary = computed(() => {
      if (totalCount.value === 0) return "No events found";
      const start = (currentPage.value - 1) * pageSize.value + 1;
      const end = Math.min(currentPage.value * pageSize.value, totalCount.value);
      return `Showing ${start}\u2013${end} of ${totalCount.value} events`;
    });

    const aggregateSourcesTreeNode = computed(() => {
      if (!aggregateSources.value || aggregateSources.value.length === 0) return [];
      return aggregateSources.value.map(aggregateSource => {
        return {
          key: aggregateSource.name,
          label: aggregateSource.name + " events",
          icon: aggregateSourceToIcon(aggregateSource.name),
          children: aggregateSource.aggregateIdentities.map(identity => {
            return {
              key: identity.id,
              label: identity.name
            };
          })
        };
      });
    });

    function aggregateSourceToIcon(aggregateSource) {
      switch (aggregateSource) {
        case "User":
          return "fa fa-user";
        case "UserMemberCard":
          return "fa fa-credit-card";
        case "UserPerk":
          return "fa fa-gift";
        case "UserLocation":
          return "fa fa-map-marker";
        case "UserPromoCode":
          return "fa fa-ticket";
        default:
          return "pi pi-question";
      }
    }

    function buildFilterParams() {
      const params = {
        page: currentPage.value,
        pageSize: pageSize.value
      };

      if (fromDate.value) {
        params.fromDate = fromDate.value.toISOString();
      }
      if (toDate.value) {
        params.toDate = toDate.value.toISOString();
      }

      const checkedIds = Object.entries(selectedAggregateSources.value)
        .filter(([, val]) => val.checked)
        .map(([key]) => key);
      if (checkedIds.length > 0) {
        params.aggregateIds = checkedIds.join(",");
      }

      if (selectedAggregateOrigins.value.length > 0) {
        params.eventSources = selectedAggregateOrigins.value.join(",");
      }

      return params;
    }

    let filterDebounceTimer = null;

    async function fetchEventLog() {
      clearTimeout(filterDebounceTimer);
      loading.value = true;

      const params = buildFilterParams();
      await store.dispatch("users/getUserEventLog", { userId, params });

      const result = store.getters["users/eventLogWrapper"];
      if (result) {
        eventLog.value = result.eventLog;
        aggregateIdentityLookup.value = result.aggregateIdentityLookup;
        aggregateSources.value = result.aggregateSources;
        totalCount.value = result.totalCount;
        totalPages.value = result.totalPages;
        currentPage.value = result.page;
      }

      loading.value = false;
    }

    function onPageChange(event) {
      currentPage.value = event.page + 1;
      fetchEventLog();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    watch(
      [fromDate, toDate, selectedAggregateSources, selectedAggregateOrigins],
      () => {
        clearTimeout(filterDebounceTimer);
        filterDebounceTimer = setTimeout(() => {
          currentPage.value = 1;
          fetchEventLog();
        }, 600);
      },
      { deep: true }
    );

    onBeforeUnmount(() => {
      clearTimeout(filterDebounceTimer);
    });

    onMounted(async () => {
      await fetchEventLog();
    });

    return {
      pageTitle,
      pageDescription,
      loading,

      eventLog,
      aggregateIdentityLookup,

      aggregateSources,
      aggregateSourceToIcon,
      aggregateSourcesTreeNode,
      selectedAggregateSources,

      aggregateOrigins,
      selectedAggregateOrigins,

      filtersCollapsed,

      fromDate,
      toDate,

      currentPage,
      pageSize,
      totalCount,
      totalPages,
      paginationSummary,

      formatDateTimeFullTimezone: useFormatDate().formatDateTimeFullTimezone,

      fetchEventLog,
      onPageChange
    };
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.aggregate-color-User {
  color: #2196f3;
}
.aggregate-bgcolor-User {
  background-color: #2196f3;
}
.aggregate-color-UserMemberCard {
  color: #4caf50;
}
.aggregate-bgcolor-UserMemberCard {
  background-color: #4caf50;
}
.aggregate-color-UserPerk {
  color: #ff9800;
}
.aggregate-bgcolor-UserPerk {
  background-color: #ff9800;
}
.aggregate-color-UserLocation {
  color: #9c27b0;
}
.aggregate-bgcolor-UserLocation {
  background-color: #9c27b0;
}
.aggregate-color-UserPromoCode {
  color: #f44336;
}
.aggregate-bgcolor-UserPromoCode {
  background-color: #f44336;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.filters-card {
  top: 214px;
  left: 280px;

  transition: left 0.3s ease-in-out;

  .filters-content {
    transition: opacity 0.3s ease-in-out;
    opacity: 1;
  }

  &.collapsed {
    left: -48px;

    .filters-content {
      pointer-events: none;
      opacity: 0;
    }
  }
}
</style>
